/**
 * TrivenyTeamPanel — Slack-style "Invite teammates" surface for a Triveny
 * workspace. A solo workspace owner can build a team around themselves:
 *
 *   - See current members + their roles (you, admins, members, viewers)
 *   - Invite a teammate by email with a role (Admin / Member / Viewer)
 *   - Copy a shareable accept link (zero email dependency — Slack-style)
 *   - Revoke a pending invitation
 *
 * Backed by the self-contained team API on the platform JWT:
 *   GET    /api/platform/auth/team
 *   POST   /api/platform/auth/team/invite
 *   DELETE /api/platform/auth/team/invite/{id}
 *
 * Styling uses the Triveny design primitives (TvCard/TvButton/TvPill/tvTokens)
 * so it matches the rest of the app. Triveny-only.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Users,
  Mail,
  Copy,
  Check,
  Trash2,
  Send,
  Loader2,
  AlertCircle,
  Crown,
  Eye,
  User as UserIcon,
} from 'lucide-react';
import { config } from '@/src/config';
import { useTabbedChrome } from '@/components/weavein';
import {
  TvCard,
  TvButton,
  TvPill,
  TvLabel,
  TvEyebrow,
  tvTokens,
} from '@/src/platform/shared/components/triveny';

type Role = 'admin' | 'member' | 'viewer';

interface Member {
  id: string;
  email: string;
  name: string;
  role: Role;
  role_key: string;
  is_active: boolean;
  is_you: boolean;
  last_login_at: string | null;
  created_at: string | null;
}

type InviteStatus = 'waiting' | 'accepted' | 'expired' | 'held';

interface Invite {
  id: string;
  email: string;
  role: Role;
  role_key: string;
  status: InviteStatus;
  created_at: string | null;
  expires_at: string | null;
  accepted_at: string | null;
  link: string;
  send_error: string | null;
}

interface TeamResponse {
  tenant: { id: string; slug: string };
  members: Member[];
  invites: Invite[];
  can_manage: boolean;
}

const ROLE_META: Record<Role, { label: string; icon: typeof UserIcon; hint: string }> = {
  admin: { label: 'Admin', icon: Crown, hint: 'Full access — manage the workspace, billing, and team.' },
  member: { label: 'Member', icon: UserIcon, hint: 'Can use every tool and work the pipeline.' },
  viewer: { label: 'Viewer', icon: Eye, hint: 'Read-only access to the workspace.' },
};

// The invite dropdown uses platform role_keys (tenant_user, custom roles, …)
// that aren't in ROLE_META. Never index ROLE_META directly — a miss returns
// undefined and crashes the page (e.g. ROLE_META['tenant_user'].hint). Fall back
// to a humanized label so any role renders safely.
function roleMeta(role: string) {
  return (
    ROLE_META[role as Role] ?? {
      label: role ? role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'Member',
      icon: UserIcon,
      hint: '',
    }
  );
}

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('auth_token') || '';
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

const INVITE_STATUS_META: Record<InviteStatus, { label: string; variant: string }> = {
  waiting: { label: 'Waiting', variant: 'attention' },
  accepted: { label: 'Accepted', variant: 'success' },
  expired: { label: 'Expired', variant: 'neutral' },
  held: { label: 'On hold', variant: 'neutral' },
};

function inviteStatusMeta(status: InviteStatus) {
  return INVITE_STATUS_META[status] ?? INVITE_STATUS_META.waiting;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function rolePill(role: Role, isYou?: boolean) {
  const variant = role === 'admin' ? 'accent' : role === 'viewer' ? 'neutral' : 'success';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <TvPill variant={variant as any}>{roleMeta(role).label}</TvPill>
      {isYou && <TvPill variant="neutral">YOU</TvPill>}
    </span>
  );
}

export default function TrivenyTeamPanel() {
  const tabbed = useTabbedChrome();
  const base = config.weaveinApi?.baseUrl || '';
  const [data, setData] = useState<TeamResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Invite form. `role` holds a platform role_key (same role model as user
  // invites) — populated from /api/platform/roles so a team admin can invite
  // with any role defined in Roles & Permissions, not just the 3 built-ins.
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<string>('tenant_user');
  const [roleOptions, setRoleOptions] = useState<Array<{ role_key: string; display_name: string; description?: string }>>([]);
  const [inviting, setInviting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  // Last-created invite link surfaced for one-click copy (Slack-style).
  const [justInvited, setJustInvited] = useState<{ email: string; link: string; emailed: boolean } | null>(null);

  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const res = await fetch(`${base}/api/platform/auth/team`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`(${res.status})`);
      setData(await res.json());
    } catch (e: any) {
      setLoadError(`Couldn't load your team ${e?.message || ''}`.trim());
    } finally {
      setLoading(false);
    }
  }, [base]);

  useEffect(() => {
    void load();
  }, [load]);

  // Load the tenant's roles (Roles & Permissions) for the invite dropdown.
  useEffect(() => {
    let cancel = false;
    fetch(`${base}/api/platform/roles`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((rows) => {
        if (cancel || !Array.isArray(rows)) return;
        const opts = rows
          .filter((r: any) => r.is_active !== false && r.role_key !== 'platform_admin')
          .map((r: any) => ({ role_key: r.role_key, display_name: r.display_name || r.role_key, description: r.description }));
        if (opts.length) {
          setRoleOptions(opts);
          // Default to a sensible non-admin role if present.
          setRole((cur) => (opts.some((o) => o.role_key === cur) ? cur : (opts.find((o) => o.role_key === 'tenant_user')?.role_key || opts[opts.length - 1].role_key)));
        }
      })
      .catch(() => {});
    return () => { cancel = true; };
  }, [base]);

  const copy = async (key: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Fallback for non-secure contexts / older webviews.
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
      } catch {
        /* ignore */
      }
      document.body.removeChild(ta);
    }
    setCopiedKey(key);
    window.setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1800);
  };

  const submitInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    const clean = email.trim().toLowerCase();
    if (!clean || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(clean)) {
      setFormError('Enter a valid email address');
      return;
    }
    setFormError(null);
    setInviting(true);
    setJustInvited(null);
    try {
      const res = await fetch(`${base}/api/platform/auth/team/invite`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ email: clean, role }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `Invite failed (${res.status})`);
      setJustInvited({ email: body.email, link: body.link, emailed: !!body.email_sent });
      setEmail('');
      // keep the selected role for the next invite
      await load();
    } catch (e: any) {
      setFormError(e?.message || 'Could not send the invite');
    } finally {
      setInviting(false);
    }
  };

  const revoke = async (inv: Invite) => {
    if (!window.confirm(`Revoke the invitation for ${inv.email}?`)) return;
    try {
      const res = await fetch(`${base}/api/platform/auth/team/invite/${inv.id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok && res.status !== 404) throw new Error(`(${res.status})`);
      setData((d) => (d ? { ...d, invites: d.invites.filter((i) => i.id !== inv.id) } : d));
      if (justInvited?.email === inv.email) setJustInvited(null);
    } catch {
      // Re-sync from server on any failure so the list reflects reality.
      void load();
    }
  };

  const canManage = data?.can_manage ?? false;

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '28px 20px 80px', fontFamily: tvTokens.fontSans }}>
      <div style={{ marginBottom: 22 }}>
        {!tabbed && (
          <>
            <TvEyebrow>Workspace</TvEyebrow>
            <h1
              style={{
                fontFamily: tvTokens.fontDisplay,
                fontSize: 26,
                fontWeight: 600,
                letterSpacing: '-0.02em',
                color: tvTokens.ink,
                margin: '6px 0 4px',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <Users size={24} color={tvTokens.accent} /> Team
            </h1>
          </>
        )}

        <p style={{ color: tvTokens.ink500, fontSize: 14, margin: 0 }}>
          Invite teammates to your workspace. They get their own login and pick up right where the team is working.
        </p>
      </div>

      {/* Invite form */}
      {canManage && (
        <TvCard header={<TvLabel>Invite a teammate</TvLabel>} style={{ marginBottom: 18 }} bodyPadding={16}>
          <form onSubmit={submitInvite} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ position: 'relative', flex: '1 1 260px' }}>
                <Mail
                  size={16}
                  color={tvTokens.ink400}
                  style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)' }}
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="teammate@company.com"
                  autoComplete="off"
                  style={{
                    width: '100%',
                    height: 38,
                    padding: '0 12px 0 34px',
                    borderRadius: tvTokens.r,
                    border: `1px solid ${tvTokens.border}`,
                    background: tvTokens.paper,
                    color: tvTokens.ink,
                    fontSize: 14,
                    outline: 'none',
                  }}
                />
              </div>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                title={roleOptions.find((o) => o.role_key === role)?.description || ''}
                style={{
                  height: 38,
                  padding: '0 12px',
                  borderRadius: tvTokens.r,
                  border: `1px solid ${tvTokens.border}`,
                  background: tvTokens.paper,
                  color: tvTokens.ink,
                  fontSize: 14,
                  outline: 'none',
                }}
              >
                {roleOptions.length > 0 ? (
                  roleOptions.map((o) => (
                    <option key={o.role_key} value={o.role_key}>{o.display_name}</option>
                  ))
                ) : (
                  <>
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                    <option value="viewer">Viewer</option>
                  </>
                )}
              </select>
              <TvButton variant="primary" icon={inviting ? undefined : Send} type="submit" disabled={inviting}>
                {inviting ? (
                  <>
                    <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Sending…
                  </>
                ) : (
                  'Send invite'
                )}
              </TvButton>
            </div>
            <p style={{ color: tvTokens.ink400, fontSize: 12.5, margin: 0 }}>{roleMeta(role).hint}</p>
            {formError && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, color: tvTokens.danger, fontSize: 13 }}>
                <AlertCircle size={15} /> {formError}
              </div>
            )}
          </form>

          {/* Slack-style: surface a copyable link right after an invite is created. */}
          {justInvited && (
            <div
              style={{
                marginTop: 14,
                padding: 13,
                borderRadius: tvTokens.r,
                background: tvTokens.accentSoft,
                border: `1px solid ${tvTokens.accent}`,
              }}
            >
              <div style={{ fontSize: 13, color: tvTokens.ink, fontWeight: 600, marginBottom: 6 }}>
                Invite ready for {justInvited.email}
                {justInvited.emailed ? ' — we emailed it too.' : ' — share this link with them.'}
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  readOnly
                  value={justInvited.link}
                  onFocus={(e) => e.currentTarget.select()}
                  style={{
                    flex: 1,
                    height: 34,
                    padding: '0 10px',
                    borderRadius: tvTokens.rSm,
                    border: `1px solid ${tvTokens.border}`,
                    background: tvTokens.paper,
                    color: tvTokens.ink700,
                    fontSize: 12.5,
                    fontFamily: tvTokens.fontMono,
                  }}
                />
                <TvButton
                  variant="ink"
                  icon={copiedKey === 'new' ? Check : Copy}
                  onClick={() => copy('new', justInvited.link)}
                >
                  {copiedKey === 'new' ? 'Copied' : 'Copy link'}
                </TvButton>
              </div>
            </div>
          )}
        </TvCard>
      )}

      {/* Invitations — every invite with its lifecycle status (waiting / accepted /
          expired / on hold), not just the currently-open ones. */}
      {(data?.invites?.length ?? 0) > 0 && (
        <TvCard
          header={<TvLabel>Invitations</TvLabel>}
          headerActions={(() => {
            const waiting = data!.invites.filter((x) => x.status === 'waiting').length;
            return waiting > 0 ? (
              <TvPill variant="attention">{waiting} waiting</TvPill>
            ) : (
              <TvPill variant="neutral">{data!.invites.length}</TvPill>
            );
          })()}
          style={{ marginBottom: 18 }}
        >
          {data!.invites.map((inv, i) => {
            const st = inviteStatusMeta(inv.status);
            const canCopy = inv.status === 'waiting' || inv.status === 'held';
            // Accepted people are managed in Members; keep the record but no revoke.
            const canRemove = canManage && inv.status !== 'accepted';
            const subLine =
              inv.status === 'accepted'
                ? `Accepted${inv.accepted_at ? ` ${fmtDate(inv.accepted_at)}` : ''}`
                : inv.status === 'expired'
                ? `Expired${inv.expires_at ? ` ${fmtDate(inv.expires_at)}` : ''}`
                : inv.status === 'held'
                ? 'On hold — not yet sent'
                : `Invited${inv.created_at ? ` ${fmtDate(inv.created_at)}` : ''}${
                    inv.send_error ? ' · email not delivered — share the link' : ''
                  }`;
            return (
              <div
                key={inv.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 16px',
                  borderTop: i === 0 ? 'none' : `1px solid ${tvTokens.borderMuted}`,
                  opacity: inv.status === 'expired' ? 0.7 : 1,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, color: tvTokens.ink, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {inv.email}
                  </div>
                  <div style={{ fontSize: 12, color: tvTokens.ink400 }}>{subLine}</div>
                </div>
                <TvPill variant={st.variant as any}>{st.label}</TvPill>
                {rolePill(inv.role)}
                {canCopy && (
                  <TvButton
                    variant="ghost"
                    icon={copiedKey === inv.id ? Check : Copy}
                    onClick={() => copy(inv.id, inv.link)}
                  >
                    {copiedKey === inv.id ? 'Copied' : 'Copy link'}
                  </TvButton>
                )}
                {canRemove && (
                  <button
                    onClick={() => revoke(inv)}
                    title={inv.status === 'expired' ? 'Remove expired invitation' : 'Revoke invitation'}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 32,
                      height: 32,
                      borderRadius: tvTokens.r,
                      border: `1px solid ${tvTokens.border}`,
                      background: tvTokens.surface,
                      color: tvTokens.ink500,
                      cursor: 'pointer',
                    }}
                  >
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            );
          })}
        </TvCard>
      )}

      {/* Members */}
      <TvCard
        header={<TvLabel>Members</TvLabel>}
        headerActions={<TvPill variant="neutral">{data?.members?.length ?? 0}</TvPill>}
      >
        {loading ? (
          <div style={{ padding: 24, display: 'flex', alignItems: 'center', gap: 8, color: tvTokens.ink400 }}>
            <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Loading…
          </div>
        ) : loadError ? (
          <div style={{ padding: 20, display: 'flex', alignItems: 'center', gap: 8, color: tvTokens.danger, fontSize: 14 }}>
            <AlertCircle size={16} /> {loadError}
          </div>
        ) : (
          (data?.members ?? []).map((m, i) => {
            const Icon = roleMeta(m.role).icon;
            return (
              <div
                key={m.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 16px',
                  borderTop: i === 0 ? 'none' : `1px solid ${tvTokens.borderMuted}`,
                  opacity: m.is_active ? 1 : 0.55,
                }}
              >
                <div
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: '50%',
                    background: tvTokens.paper2,
                    border: `1px solid ${tvTokens.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: tvTokens.ink500,
                    flexShrink: 0,
                  }}
                >
                  <Icon size={16} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, color: tvTokens.ink, fontWeight: 500 }}>{m.name}</div>
                  <div style={{ fontSize: 12, color: tvTokens.ink400, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {m.email}
                  </div>
                </div>
                {rolePill(m.role, m.is_you)}
              </div>
            );
          })
        )}
      </TvCard>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
